/**
 * Touch **pinch-to-zoom** gesture maths: the pure decision core behind the
 * mobile "spread two fingers to zoom in, pinch them together to zoom out"
 * interaction (issue #170, PRD #169). It is a sibling of the tap-to-walk core
 * {@link autowalk} - like it, this holds **no DOM and no Pixi**: `main.ts` owns
 * the touch listeners and the {@link GameRenderer.setZoom} drive, this owns the
 * gesture geometry so it unit-tests in node.
 *
 * Pinch is "the wheel, driven by two fingers": it produces only a zoom
 * *magnitude*, which the glue feeds to the renderer's existing, already
 * clamped + smoothed zoom pipeline ({@link GameRenderer.setZoom}). There is no
 * new zoom state and no focal/camera offset - zoom stays player-centred, exactly
 * like the desktop wheel (the follow-camera keeps the player centred). See
 * `docs/adr/0093-pinch-to-zoom-tracer.md`.
 *
 * The three function signatures are the PRD's final ones and form the **stable
 * seam** the later slices build on without touching callers: #171 (feel &
 * sensitivity hardening) swaps the body of {@link zoomForPinch} - adding a gain
 * exponent, smoothing, degenerate-input guards - behind this same boundary, and
 * #172 (on-screen ± buttons) reuses the renderer drive. Keep the boundary fixed;
 * change the bodies.
 *
 * **#171 hardening (this slice, ADR-0095):** {@link zoomForPinch} now carries the
 * {@link PINCH_GAIN} sensitivity exponent (kept at the 1:1 default - the renderer's
 * existing ~67ms eased `targetZoom` already gives the gesture a pleasant settle, so
 * no extra gain was warranted in-browser) and the **degenerate-input guards**: a
 * coincident/zero/NaN baseline span, or any span that would produce a non-finite or
 * non-positive zoom, now *holds* the baseline zoom instead of dividing by zero and
 * feeding `NaN` into the renderer (`clampZoom` does **not** filter `NaN`, so an
 * unguarded `0/0` here would corrupt `targetZoom` and freeze the view). Signatures
 * are unchanged; only the bodies hardened.
 */

/**
 * Pinch **sensitivity exponent**: {@link zoomForPinch} raises the live/baseline span
 * ratio to this power, so `1` is exact 1:1 finger tracking, `<1` damps the gesture
 * (a given spread changes zoom less - calmer), and `>1` sharpens it. Kept at `1`
 * (#171): the headless two-pointer pass across the full `[0.5, 3]` range felt
 * controlled and predictable with the renderer's existing easing, so the simplest
 * mapping wins. The exponent is in place behind the stable seam to be tuned against a
 * real device without touching callers or the signature.
 */
export const PINCH_GAIN = 1;

/**
 * A screen-space point (canvas pixels). Structurally compatible with the
 * `{ x, y }` the glue reads off a `PointerEvent`, so no adapter is needed.
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * The Euclidean span between the two live touch points - the gesture's "size".
 * Spreading the fingers grows it (zoom in), pinching them shrinks it (zoom out);
 * {@link zoomForPinch} reads the ratio of the live span to the baseline span
 * captured when the second finger landed.
 */
export function pinchDistance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * The midpoint between the two touches. Kept for completeness and for the later
 * slices (a button/affordance anchor, telemetry); the player-centred tracer does
 * **not** consume it for a focal offset - zoom is magnitude-only here.
 */
export function pinchMidpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * The zoom magnitude a pinch should drive the renderer to: the baseline zoom
 * scaled by how much the fingers have spread or closed since the gesture began -
 * `baselineZoom * (liveDist / baselineDist) ** PINCH_GAIN`. Spreading
 * (`liveDist > baselineDist`) zooms in, pinching (`liveDist < baselineDist`) zooms
 * out, proportionally and continuously; {@link PINCH_GAIN} (default `1`) sets how
 * hard the ratio bites. The result is an **unclamped** magnitude: the renderer's
 * {@link GameRenderer.setZoom} applies the `[MIN_ZOOM, MAX_ZOOM]` clamp and the
 * easing, so the gesture saturates cleanly at the bounds for free.
 *
 * **Degenerate-input guards (#171).** `baselineDist` is the span captured when the
 * second finger landed - normally non-zero (two distinct touches) but not
 * guaranteed: two touch points can report identical coordinates, making the span
 * `0`. A bare `liveDist / baselineDist` then yields `Infinity` (live apart) or `NaN`
 * (`0/0`, both coincident). `clampZoom` lets `NaN` straight through
 * (`Math.min(3, Math.max(0.5, NaN)) === NaN`), so that would poison `targetZoom` and
 * freeze the camera. So: a non-positive/`NaN` baseline, or any computed magnitude
 * that is not a finite positive number, **holds the baseline zoom** - the only safe,
 * jump-free response (the gesture simply doesn't move the zoom until the spans are
 * real again). All bound-saturation past `[0.5, 3]` is still the renderer's job.
 */
export function zoomForPinch(baselineZoom: number, baselineDist: number, liveDist: number): number {
  // Coincident/zero/NaN baseline (or a non-finite live span): no valid ratio exists,
  // so hold the baseline rather than divide-by-zero into NaN/Infinity. `!(d > 0)`
  // also catches NaN, which compares false to everything.
  if (!(baselineDist > 0) || !(liveDist >= 0) || !Number.isFinite(liveDist)) {
    return baselineZoom;
  }
  const ratio = liveDist / baselineDist;
  // Math.pow(r, 1) === r exactly, so the default gain preserves the bare 1:1 ratio
  // (and its exact-equality contract) with no floating-point drift.
  const zoom = baselineZoom * (PINCH_GAIN === 1 ? ratio : Math.pow(ratio, PINCH_GAIN));
  // A momentary zero/degenerate magnitude (e.g. fingers driven fully coincident)
  // holds the last good zoom rather than snapping the world scale to 0 or NaN.
  return Number.isFinite(zoom) && zoom > 0 ? zoom : baselineZoom;
}
